import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Home = () => {
  const { t } = useTranslation();
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setShowScrollIndicator(false);
      } else {
        setShowScrollIndicator(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="military-hero">
      {/* Military Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center p-4">
        {/* Military Grid Background */}
        <div className="absolute inset-0 military-grid opacity-20 z-background"></div>
        
        {/* Military Dots Pattern */}
        <div className="absolute inset-0 military-dots opacity-10 z-background"></div>

        {/* Military Geometric Elements */}
        <div className="absolute inset-0 z-background">
          <div className="absolute top-20 left-20 w-32 h-32 border border-military-accent/30 transform rotate-45"></div>
          <div className="absolute top-40 right-32 w-24 h-24 border border-military-accent/40 transform -rotate-12"></div>
          <div className="absolute bottom-32 left-1/4 w-16 h-16 border border-military-accent/20 transform rotate-30"></div>
          <div className="absolute bottom-20 right-1/3 w-20 h-20 border border-military-accent/30 transform -rotate-45"></div>
        </div>

        {/* Scanning Line Effect */}
        <div className="absolute inset-0 z-background overflow-hidden">
          <motion.div
            className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-military-accent to-transparent"
            animate={{ y: [0, window.innerHeight, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          />
        </div>



        {/* Main Content */}
        <div className="relative z-hero-content max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2 }}
            className="mb-8"
          >
                                                             <h1 className="text-6xl sm:text-8xl md:text-12xl font-black tracking-tighter leading-none mb-4 text-gradient-military">
                           {t('home.title', 'CENTRON')}
                         </h1>
                         <h2 className="text-2xl sm:text-4xl md:text-6xl font-bold tracking-widest text-military-silver uppercase">
                           {t('home.subtitle', 'AEROSPACE')}
                         </h2>
                         <div className="text-sm sm:text-lg md:text-xl font-military-mono tracking-widest text-military-steel uppercase mt-4">
                           {t('home.tagline', 'DEFENSE & ENGINEERING')}
                         </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mb-12"
          >
                                    <p className="text-base sm:text-xl md:text-2xl font-military-mono text-military-silver max-w-4xl mx-auto leading-relaxed px-4">
                          {t('home.description', 'Advanced tactical UAV systems, cruise missile technology, and cutting-edge aerospace engineering solutions for national defense and global security.')}
                        </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center px-4"
          >
            <Link 
              to="/uav-platform" 
              className="btn-military-primary text-sm sm:text-base"
            >
              {t('home.uavPlatform', 'UAV PLATFORM')}
            </Link>
            
            <Link 
              to="/rd" 
              className="btn-military-secondary text-sm sm:text-base"
            >
              {t('home.rdProjects', 'R&D PROJECTS')}
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ 
            opacity: showScrollIndicator ? 1 : 0,
            y: showScrollIndicator ? 0 : 20,
            scale: showScrollIndicator ? 1 : 0.8
          }}
          transition={{ 
            duration: 0.6, 
            ease: "easeInOut"
          }}
          className="scroll-indicator z-scroll-indicator"
        >
          <div className="flex flex-col items-center">
            <motion.span 
              className="text-sm font-military-mono mb-3 tracking-widest text-military-accent"
              animate={{ 
                opacity: [0.7, 1, 0.7]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity, 
                ease: "easeInOut"
              }}
            >
              {t('home.scroll', 'SCROLL')}
            </motion.span>
            
            <motion.div
              animate={{ 
                y: [0, 8, 0]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity, 
                ease: "easeInOut"
              }}
              className="w-1 h-6 bg-military-accent rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Military Capabilities Section */}
      <section className="section-military bg-gray-900">
        <div className="container-military">
          <motion.div
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-16"
          >
                         <h2 className="text-4xl sm:text-6xl md:text-8xl lg:text-military-huge text-gradient-military mb-4 px-4">
               {t('home.militaryCapabilities')}
             </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-military-accent to-military-accent"></div>
          </motion.div>

          <div className="grid-military">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="lg:col-span-8 military-panel p-12 relative"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-military-accent/10 transform rotate-45 translate-x-16 -translate-y-16"></div>
                             <h3 className="text-4xl font-black tracking-tighter mb-6 text-gradient-military">
                 {t('home.advancedTacticalUAV', 'ADVANCED TACTICAL UAV')}
               </h3>
               <p className="text-xl leading-relaxed mb-8 text-military-silver">
                 {t('home.advancedTacticalUAVDesc', 'High-performance unmanned aerial vehicles designed for reconnaissance, surveillance, and tactical operations. Featuring advanced stealth technology and extended range capabilities.')}
               </p>
              <div className="w-16 h-1 bg-gradient-to-r from-military-accent to-military-accent"></div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="lg:col-span-4 military-card"
            >
              <div className="text-6xl font-black mb-4 text-gradient-military">01</div>
                             <h4 className="text-2xl font-bold mb-4 text-military-accent">
                 {t('home.stealthTechnology', 'STEALTH TECHNOLOGY')}
               </h4>
               <p className="text-military-silver">
                 {t('home.stealthTechnologyDesc', 'Advanced radar-absorbing materials and aerodynamic design for maximum operational security.')}
               </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="lg:col-span-4 military-card"
            >
              <div className="text-6xl font-black mb-4 text-gradient-military">02</div>
                             <h4 className="text-2xl font-bold mb-4 text-military-accent">
                 {t('home.cruiseMissileSystems', 'CRUISE MISSILE SYSTEMS')}
               </h4>
               <p className="text-military-silver">
                 {t('home.cruiseMissileSystemsDesc', 'Precision-guided missile technology with advanced targeting and navigation systems.')}
               </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="lg:col-span-8 military-panel p-12"
            >
                             <h3 className="text-4xl font-black tracking-tighter mb-6 text-gradient-military">
                 {t('home.fullMissionCapability', 'FULL MISSION CAPABILITY')}
               </h3>
               <p className="text-xl leading-relaxed mb-8 text-military-silver">
                 {t('home.fullMissionCapabilityDesc', 'From concept to deployment, we provide complete mission solutions with cutting-edge technology and proven expertise in defense systems engineering.')}
               </p>
              <div className="w-16 h-1 bg-gradient-to-r from-military-accent to-military-accent"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Aerospace Technology Section */}
      <section className="section-military bg-black">
        <div className="container-military">
          <motion.div
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-16"
          >
                         <h2 className="text-4xl sm:text-6xl md:text-8xl lg:text-military-huge text-gradient-military mb-4 px-4">
               {t('home.aerospaceTechnology')}
             </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-military-accent to-military-accent"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                         {[
               { 
                 title: t('home.uavFighter', 'UAV FIGHTER'), 
                 description: t('home.uavFighterDesc', 'Advanced combat UAV with air-to-air and air-to-ground capabilities'),
                 icon: "🛩️",
                 gradient: "from-military-accent to-military-blue"
               },
               { 
                 title: t('home.intelligentCruiseMissile', 'INTELLIGENT CRUISE MISSILE'), 
                 description: t('home.intelligentCruiseMissileDesc', 'AI-powered precision strike systems with autonomous navigation'),
                 icon: "🚀",
                 gradient: "from-military-gold to-military-warning"
               },
               { 
                 title: t('home.tacticalPlatforms', 'TACTICAL PLATFORMS'), 
                 description: t('home.tacticalPlatformsDesc', 'Multi-role UAV platforms for various military applications'),
                 icon: "⚡",
                 gradient: "from-military-success to-military-accent"
               }
             ].map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="military-card group cursor-pointer"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">{tech.icon}</div>
                <h3 className="text-2xl font-black tracking-tighter mb-4 text-gradient-military">{tech.title}</h3>
                <p className="text-military-silver group-hover:text-white transition-colors duration-300">
                  {tech.description}
                </p>
                <div className={`w-full h-1 bg-gradient-to-r ${tech.gradient} mt-4 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Military Stats */}
      <section className="section-military bg-gray-900">
        <div className="container-military">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                         {[
               { number: "15+", label: t('home.yearsExperience', 'YEARS EXPERIENCE'), color: "text-gradient-military" },
               { number: "50+", label: t('home.militaryProjects', 'MILITARY PROJECTS'), color: "text-gradient-gold" },
               { number: "100%", label: t('home.missionSuccess', 'MISSION SUCCESS'), color: "text-gradient-military" },
               { number: "24/7", label: t('home.supportAvailable', 'SUPPORT AVAILABLE'), color: "text-gradient-gold" }
             ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="military-card-inverse text-center group cursor-pointer"
              >
                <div className={`text-4xl md:text-6xl font-black mb-2 ${stat.color} group-hover:scale-110 transition-transform duration-300`}>
                  {stat.number}
                </div>
                <div className="text-sm font-military-mono tracking-widest uppercase text-military-silver group-hover:text-white transition-colors duration-300">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Defense Contact CTA */}
      <section className="section-military bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
                                                             <h2 className="text-4xl sm:text-6xl md:text-8xl lg:text-military-huge text-gradient-military mb-8 px-4">
                           {t('home.defenseReady')}
                         </h2>
                         <p className="text-xl mb-12 max-w-2xl mx-auto text-military-silver">
                           {t('home.defenseReadyDesc', 'Let\'s discuss your defense requirements and explore how Centron Aerospace can deliver exceptional military-grade solutions.')}
                         </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
                             <Link 
                 to="/contact" 
                 className="btn-military-primary"
               >
                 {t('home.contactUs')}
               </Link>
               <Link 
                 to="/uav-platform" 
                 className="btn-military-secondary"
               >
                 {t('home.viewPlatforms')}
               </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home; 